import { Popover, PopoverContent, PopoverTrigger, Tooltip } from '@nextui-org/react'
import { useMutation } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { TaskStatuses } from '~/constants/task-status'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { AgentTaskStatus, CommandType, commandTypeFromJSON } from '~/protos/agents'
import { cancelTask, deleteTask } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { Button } from '../common/Button'
import { StatusPill } from '../common/StatusPill'
import { Typography } from '../common/Typography'

const TERMINATED_GROUPS = new Set(['COMPLETED', 'ERRORED', 'STOPPED'])

const CANCELLABLE_COMMANDS = new Set([
  CommandType.GENERATE_SIMPLE_CODE_PR,
  CommandType.MUNIT_TEST_CREATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
  CommandType.MIGRATION_ACCELERATOR,
  CommandType.MIGRATION_DOCUMENT,
  CommandType.INTEGRATION_TEST,
  CommandType.MULE_VERSION_UPGRADE
])

export interface TaskItemProps {
  taskId: number
  title: string
  taskStatus: AgentTaskStatus
  conversationUuid: string
  isSelected?: boolean
  onClick?: () => void
  className?: string
  noHover?: boolean
  cmdType?: string | number
  isCancelRequested?: boolean
  /**
   * When set, this task was spawned by the schedule materializer. We render
   * a small "Scheduled" indicator next to the status pill so users can tell
   * scheduled runs apart from ad-hoc ones at a glance.
   */
  scheduledTaskId?: number | null
}

export const TaskItem: React.FC<TaskItemProps> = ({
  taskId,
  title,
  taskStatus,
  conversationUuid,
  isSelected = false,
  onClick,
  className,
  noHover = false,
  cmdType,
  isCancelRequested = false,
  scheduledTaskId
}) => {
  const status = TaskStatuses[taskStatus]
  const [isOpen, setIsOpen] = useState(false)
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { conversations, refetch } = useConversationsContext()

  const commandType = cmdType !== undefined ? commandTypeFromJSON(cmdType) : undefined
  const isCancellableCommand = commandType !== undefined && CANCELLABLE_COMMANDS.has(commandType)
  const isTerminated = TERMINATED_GROUPS.has(status.group)
  const showCancelButton = isCancellableCommand && !isTerminated && !isCancelRequested

  // Navigate away from the task that is about to be deleted, if it is the one
  // currently open. This must happen BEFORE the delete request so the open
  // task detail query (TaskProvider/useTaskDetails) unmounts and never
  // re-fetches the now-deleted task — otherwise it 404s and shows a spurious
  // "Task not found" error toast alongside the success toast.
  const navigateAwayIfActive = () => {
    const activeTaskId = Number(searchParams.get('task_id'))
    if (activeTaskId !== taskId) return

    // Redirect to the next task in the same sidebar order after deletion.
    const orderedTasks = conversations
      .flatMap(conversation =>
        (conversation.tasks || []).map(task => ({
          taskId: task.task_id,
          conversationUuid: conversation.uuid
        }))
      )
      .sort((a, b) => b.taskId - a.taskId)

    const currentIndex = orderedTasks.findIndex(task => task.taskId === taskId)
    const remainingTasks = orderedTasks.filter(task => task.taskId !== taskId)
    const redirectTask =
      currentIndex >= 0
        ? remainingTasks[currentIndex] || remainingTasks[currentIndex - 1]
        : remainingTasks[0]

    if (redirectTask) {
      const redirectUrl = workspaceUuid
        ? `/w/${workspaceUuid}/c/${redirectTask.conversationUuid}?task_id=${redirectTask.taskId}`
        : `/c/${redirectTask.conversationUuid}?task_id=${redirectTask.taskId}`
      navigate(redirectUrl, { replace: true })
    } else {
      const conversationUrl = workspaceUuid
        ? `/w/${workspaceUuid}/c/${conversationUuid}`
        : `/c/${conversationUuid}`
      navigate(conversationUrl, { replace: true })
    }
  }

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!workspaceUuid) throw new Error('Workspace UUID not found')
      // Deselect/navigate away from the open task first, then delete.
      navigateAwayIfActive()
      return deleteTask(workspaceUuid, conversationUuid, taskId).catch(err => {
        // Delete failed — navigate back so the user isn't stranded away from the still-existing task.
        navigate(`/w/${workspaceUuid}/c/${conversationUuid}?task_id=${taskId}`, { replace: true })
        throw err
      })
    },
    onSuccess: () => {
      refetch()
      BetterToast.success('Task deleted successfully')
    },
    onError: error => {
      console.error('Failed to delete task:', error)
      BetterToast.error('Failed to delete task')
      setIsOpen(false)
    }
  })

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!workspaceUuid) throw new Error('Workspace UUID not found')
      return cancelTask({ workspaceUuid, uuid: conversationUuid, taskId })
    },
    onSuccess: () => {
      refetch()
      BetterToast.success('Cancel requested')
    },
    onError: error => {
      console.error('Failed to cancel task:', error)
      BetterToast.error('Failed to cancel task')
    }
  })

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await deleteMutation.mutateAsync()
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to delete task:', error)
    }
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    cancelMutation.mutate()
  }

  return (
    <div
      className={cm(
        'py-3 px-4 cursor-pointer rounded-[16px] select-none group relative',
        noHover ? '' : 'hover:bg-tokens-neutral-fill-fill-4 transition-colors',
        isSelected ? 'bg-tokens-neutral-fill-fill-4' : 'bg-transparent',
        className
      )}
      onClick={onClick}
    >
      <div className='relative'>
        <AnimatePresence mode='wait'>
          <motion.div
            key={`${taskId}-${title}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }}
            className='flex min-w-0 flex-col'
          >
            {/* Task ID */}
            <Typography
              fontFamily='geistMono'
              fontSize='labelMd'
              fontWeight='semibold'
              className='pr-16 text-tokens-neutral-text-tertiary'
            >
              #{taskId}
            </Typography>

            {/* Task Title */}
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='semibold'
              className='mt-1 pr-16 text-tokens-neutral-text-primary'
            >
              {title}
            </Typography>

            {/* Group status + scheduled first, but allow wrapping before anything clips. */}
            <div
              className='mt-3 flex w-full min-w-0 flex-wrap items-center gap-2'
              data-testid={`task-badge-row-${taskId}`}
            >
              <StatusPill
                status={status.group}
                text={getStatusDisplayText(status.group)}
                className='shrink-0 whitespace-nowrap'
              />
              {scheduledTaskId ? (
                <Tooltip
                  content={`Spawned by schedule #${scheduledTaskId}`}
                  placement='top'
                  delay={300}
                >
                  <span
                    className='inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-tokens-status-link-base bg-tokens-status-link-base-2 px-1.5 py-0.5'
                    data-testid={`scheduled-badge-${taskId}`}
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyTag'
                      fontWeight='semibold'
                      className='text-tokens-status-link-3 leading-none'
                    >
                      Scheduled
                    </Typography>
                  </span>
                </Tooltip>
              ) : null}
              {isCancellableCommand && isCancelRequested && (
                <Typography
                  fontFamily='geist'
                  fontSize='labelSm'
                  className='shrink-0 text-tokens-neutral-text-tertiary italic'
                >
                  Cancel Requested
                </Typography>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div
          className='absolute right-0 top-0 flex flex-shrink-0 items-center gap-1'
          data-testid={`task-action-row-${taskId}`}
        >
          {/* Cancel button for cancellable non-terminated tasks */}
          {showCancelButton && (
            <Tooltip content='Cancel task' placement='top' delay={300}>
              <Button
                variant='borderless'
                className='opacity-0 group-hover:opacity-100 min-w-[24px] w-[24px] h-[24px] p-0 flex items-center justify-center'
                onClick={handleCancel}
                isDisabled={cancelMutation.isPending}
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 14 14'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <rect
                    x='2'
                    y='2'
                    width='10'
                    height='10'
                    rx='2'
                    fill='currentColor'
                    fillOpacity='0.7'
                  />
                </svg>
              </Button>
            </Tooltip>
          )}

          {/* 3-dot Menu - Delete Only */}
          <Popover
            placement='bottom-end'
            showArrow={false}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
          >
            <PopoverTrigger>
              <Button
                variant='borderless'
                className='opacity-0 group-hover:opacity-100 min-w-[24px] w-[24px] h-[24px] p-0 flex items-center justify-center'
                onClick={e => e.stopPropagation()}
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='text-tokens-neutral-text-secondary'
                >
                  <path
                    d='M7.125 3.60938C7.125 3.84144 7.21719 4.064 7.38128 4.22809C7.54538 4.39219 7.76794 4.48438 8 4.48438C8.23206 4.48438 8.45462 4.39219 8.61872 4.22809C8.78281 4.064 8.875 3.84144 8.875 3.60938C8.875 3.37731 8.78281 3.15475 8.61872 2.99066C8.45462 2.82656 8.23206 2.73438 8 2.73438C7.76794 2.73438 7.54538 2.82656 7.38128 2.99066C7.21719 3.15475 7.125 3.37731 7.125 3.60938ZM7.125 7.98438C7.125 8.21644 7.21719 8.439 7.38128 8.60309C7.54538 8.76719 7.76794 8.85938 8 8.85938C8.23206 8.85938 8.45462 8.76719 8.61872 8.60309C8.78281 8.439 8.875 8.21644 8.875 7.98438C8.875 7.75231 8.78281 7.52975 8.61872 7.36566C8.45462 7.20156 8.23206 7.10938 8 7.10938C7.76794 7.10938 7.54538 7.20156 7.38128 7.36566C7.21719 7.52975 7.125 7.75231 7.125 7.98438ZM7.125 12.3594C7.125 12.5914 7.21719 12.814 7.38128 12.9781C7.54538 13.1422 7.76794 13.2344 8 13.2344C8.23206 13.2344 8.45462 13.1422 8.61872 12.9781C8.78281 12.814 8.875 12.5914 8.875 12.3594C8.875 12.1273 8.78281 11.9048 8.61872 11.7407C8.45462 11.5766 8.23206 11.4844 8 11.4844C7.76794 11.4844 7.54538 11.5766 7.38128 11.7407C7.21719 11.9048 7.125 12.1273 7.125 12.3594Z'
                    fill='currentColor'
                  />
                </svg>
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className='p-2'>
                <Button
                  variant='borderless'
                  className='w-full justify-start text-danger'
                  onClick={handleDelete}
                  isDisabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}

// Helper function to get display text for status
const getStatusDisplayText = (group: string): string => {
  switch (group) {
    case 'COMPLETED':
      return 'Completed'
    case 'NEED_ACTION':
      return 'Input Needed'
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'ERRORED':
      return 'Error'
    case 'STOPPED':
      return 'Stopped'
    default:
      return 'In Progress'
  }
}

export default TaskItem
